import React, { useState, useEffect, useCallback } from 'react';

import { v2, Banner, Loader } from '@fieldnation/platform-components';
import styles from './ReviewDispatchSettingsModal.scss';
import { ReviewDispatchSettingsModalProps } from './types.d';
import { getDispatchSettings, getDispatchExpansionSettings } from '../utils/dispatchSettingsApi';
import formatDispatchDuration from './utils';
import { trackEvent, SMART_DISPATCHED_WORK_ORDER, mapCriteriaToFilterNames } from './amplitude';

const { Modal } = v2;

// Extend Banner type to include action prop that exists in runtime
const ExtendedBanner = Banner as React.ComponentType<
  React.ComponentProps<typeof Banner> & {
    action?: {
      text: string;
      href: string;
      target?: '_blank' | '_self' | '_parent' | '_top';
    };
  }
>;

export const ReviewDispatchSettingsModal = ({
  isOpen,
  workOrderId,
  projectId,
  projectName,
  serviceTerritoryName,
  isSmartDispatchV3 = false,
  onClose,
  onSubmit,
}: ReviewDispatchSettingsModalProps): JSX.Element => {
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allSegmentsDisabled, setAllSegmentsDisabled] = useState(false);
  const [enabledSegments, setEnabledSegments] = useState<string[]>([]);
  const [eligibilityCriteria, setEligibilityCriteria] = useState<string[]>([]);
  const [dispatchData, setDispatchData] = useState<{
    dispatchDuration: number;
    unassignedWorkOrders: string;
  } | null>(null);

  const fetchDispatchSettings = useCallback(async () => {
    setLoading(true);
    setIsError(false);
    setAllSegmentsDisabled(false);
    setEnabledSegments([]);
    setEligibilityCriteria([]);

    try {
      const expansionPromise = isSmartDispatchV3
        ? getDispatchExpansionSettings(projectId).catch(() => null)
        : Promise.resolve(null);

      const [data, expansionData] = await Promise.all([
        getDispatchSettings(projectId),
        expansionPromise,
      ]);

      setDispatchData({
        dispatchDuration: data?.dispatch_runtime_minutes ?? 0,
        unassignedWorkOrders: data?.revert_to_draft_on_no_available
          ? 'Revert to draft'
          : 'Remain published',
      });

      if (isSmartDispatchV3 && expansionData !== null) {
        const exp = expansionData?._included?.expansionSettings;
        const noSegmentsEnabled = !exp?.serviceTerritory?.enabled
          && !exp?.marketplace?.enabled
          && !exp?.talentPools?.enabled;
        setAllSegmentsDisabled(noSegmentsEnabled);

        const segments: string[] = [];
        if (exp?.serviceTerritory?.enabled) segments.push('Service Territory');
        if (exp?.talentPools?.enabled) segments.push('Talent Pools');
        if (exp?.marketplace?.enabled) segments.push('Marketplace');
        setEnabledSegments(segments);

        setEligibilityCriteria(mapCriteriaToFilterNames(expansionData?._included?.criteria || []));
      }
    } catch (error) {
      setDispatchData(null);
      setIsError(true);
    } finally {
      setLoading(false);
    }
  }, [projectId, isSmartDispatchV3]);

  useEffect(() => {
    if (isOpen) {
      fetchDispatchSettings();
    }
  }, [isOpen, fetchDispatchSettings]);

  const showNoSegmentsBanner = isSmartDispatchV3 && allSegmentsDisabled;

  const handleDispatch = () => {
    trackEvent(SMART_DISPATCHED_WORK_ORDER, {
      'Work Order ID': [workOrderId],
      'Project ID': projectId || null,
      'Project Name': projectName || null,
      'Eligibility Criteria': eligibilityCriteria.length ? eligibilityCriteria : null,
      'Dispatch Duration': dispatchData?.dispatchDuration ?? null,
      'Dispatch Segments': enabledSegments.length ? enabledSegments : null,
      'Service Territory Name': serviceTerritoryName || null,
    });
    onSubmit();
  };

  const renderContent = () => {
    if (isError) {
      return (
        <div className={styles.error_placeholder}>
          <ExtendedBanner
            type="alert"
            message="An error occurred while fetching dispatch settings. Please try again later."
          />
        </div>
      );
    }

    if (!dispatchData) {
      return <div className={styles.empty_placeholder} />;
    }

    return (
      <div className={styles.review_dispatch_settings_modal}>
        <div className={styles.smart_dispatch_section}>
          {projectName && (
            <>
              <div className={styles.smart_dispatch_row}>
                <strong>Smart Dispatch: </strong>
                {projectName}
              </div>
              <hr className={styles.section_divider} />
            </>
          )}
          <div className={styles.smart_dispatch_row}>
            <span className={styles.dispatch_label}>Dispatch duration:</span>
            {formatDispatchDuration(dispatchData.dispatchDuration)}
          </div>
          <div className={styles.dispatch_detail_row}>
            <span className={styles.dispatch_label}>
              Unassigned work orders:
            </span>
            {dispatchData.unassignedWorkOrders}
          </div>
        </div>

        {showNoSegmentsBanner && (
          <ExtendedBanner
            type="alert"
            title="Smart Dispatch Cannot run"
            message="No provider segments are enabled. You must enable at least one segment under the segments & criteria tab."
            action={{
              text: 'Go to project dispatch settings',
              href: `/projects/${projectId}/manage-project?tab=smart-dispatch`,
              target: '_blank',
            }}
          />
        )}

        {!showNoSegmentsBanner && <ExtendedBanner
          type="info"
          message="To modify the Smart Dispatch settings for this work order, please update the project settings."
          action={{
            text: 'Go to project dispatch settings',
            href: `/projects/${projectId}/manage-project?tab=smart-dispatch`,
            target: '_blank',
          }}
        />}
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header="Review Dispatch Settings"
      size="medium"
      type="secondary"
      footerActions={[
        {
          label: 'Cancel',
          type: 'secondary',
          onClick: onClose,
          disabled: loading,
        },
        {
          label: 'Dispatch Work Order',
          type: 'primary',
          onClick: handleDispatch,
          disabled: isError || loading || showNoSegmentsBanner,
        },
      ]}
    >
      <div>
        <Loader isLoading={loading}>{renderContent()}</Loader>
      </div>
    </Modal>
  );
};

export default ReviewDispatchSettingsModal;
